import client from './client'
import type {
  SupportTicket,
  SupportMessage,
  SupportAttachment,
  StoreSupportTicketPayload,
  StoreSupportMessagePayload,
  SupportTicketFilter,
} from '@/types/support'
import type { PaginatedResponse } from '@/types/finance'

function base(organisationId: number): string {
  return `/organizer/organisations/${organisationId}/support`
}

export interface ListSupportTicketsParams {
  filter?: SupportTicketFilter
  page?: number
  per_page?: number
}

export async function listSupportTickets(
  organisationId: number,
  params: ListSupportTicketsParams = {},
): Promise<PaginatedResponse<SupportTicket>> {
  const { data } = await client.get<{ data: SupportTicket[]; meta: PaginatedResponse<SupportTicket>['meta'] }>(
    `${base(organisationId)}/tickets`,
    { params },
  )
  return { data: data.data, meta: data.meta }
}

export async function createSupportTicket(
  organisationId: number,
  payload: StoreSupportTicketPayload,
): Promise<SupportTicket> {
  const { data } = await client.post<{ data: SupportTicket }>(
    `${base(organisationId)}/tickets`,
    payload,
  )
  return data.data
}

export async function showSupportTicket(
  organisationId: number,
  ticketId: number,
): Promise<SupportTicket> {
  const { data } = await client.get<{ data: SupportTicket }>(
    `${base(organisationId)}/tickets/${ticketId}`,
  )
  return data.data
}

export async function replySupportTicket(
  organisationId: number,
  ticketId: number,
  payload: StoreSupportMessagePayload,
): Promise<SupportMessage> {
  const { data } = await client.post<{ data: SupportMessage }>(
    `${base(organisationId)}/tickets/${ticketId}/messages`,
    payload,
  )
  return data.data
}

export async function closeSupportTicket(
  organisationId: number,
  ticketId: number,
): Promise<SupportTicket> {
  const { data } = await client.patch<{ data: SupportTicket }>(
    `${base(organisationId)}/tickets/${ticketId}/close`,
  )
  return data.data
}

export async function reopenSupportTicket(
  organisationId: number,
  ticketId: number,
): Promise<SupportTicket> {
  const { data } = await client.patch<{ data: SupportTicket }>(
    `${base(organisationId)}/tickets/${ticketId}/reopen`,
  )
  return data.data
}

export async function fetchSupportUnreadCount(
  organisationId: number,
): Promise<{ count: number }> {
  const { data } = await client.get<{ data: { count: number } }>(
    `${base(organisationId)}/unread-count`,
  )
  return data.data
}

export async function uploadSupportAttachment(
  organisationId: number,
  file: File,
): Promise<SupportAttachment> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await client.post<{ data: SupportAttachment }>(
    `${base(organisationId)}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data.data
}
